import { atom, Getter, Setter } from "jotai";
import { atomWithQuery } from "jotai/query";

import { ComponentOverlayer, LayerGroup, Overlayer } from "src/types";
import { staticOverlayers } from "src/config/overlayers";
import {
  addOrUpdateInList,
  filterActive,
  residentialMapToOverlayer,
  residentialTotalMapToOverlayer,
} from "src/utils/layer-manager.utils";
import { queryFn as queryResidentialMaps } from "src/hooks/useResidentialMaps";
import { baseDataKeys } from "src/query-keys";

export const overlayersAtom = atom<Array<Overlayer | ComponentOverlayer>>((get: Getter) => {
  const activeOverlayers = get(activeOverlayersAtom);
  const residentialMaps = get(residentialMapsAtom);

  const allOverlayers = [...staticOverlayers, ...(residentialMaps ? residentialMaps : [])];

  // merge activeOverlayers with staticOverlayers list
  // isActive, opacity, ... properties can change for active ones
  return allOverlayers.map((layer) => {
    const result = activeOverlayers.find((a) => a.key === layer.key);

    if (result) return result;
    else return layer;
  });
});

const residentialMapsAtom = atomWithQuery(() => ({
  queryKey: baseDataKeys.residentialMaps,
  queryFn: async () => {
    const data = await queryResidentialMaps();
    return [
      // first total residential maps
      ...data.totalMapList.map(residentialTotalMapToOverlayer),

      // second residential maps with substance, fuel, dwelling and appliance subdivision
      ...data.mapList.map(residentialMapToOverlayer),
    ];
  },
}));

export const activeOverlayersAtom = atom<Array<Overlayer | ComponentOverlayer>>(filterActive(staticOverlayers));

// add or update overlayer
export const addOrUpdateActiveOverlayerAtom = atom(null, (get: Getter, set: Setter, update: Overlayer) => {
  set(activeOverlayersAtom, addOrUpdateInList(get(activeOverlayersAtom), update));
});

// active readio means all other overlayers from the same group are disabled
export const updateActiveRadioOverlayerAtom = atom(null, (get: Getter, set: Setter, update: Overlayer) => {
  set(
    activeOverlayersAtom,
    get(overlayersAtom).map((overlayer) => {
      // apply radio toggle functionality on layers inside the same group
      const groupMatch = overlayer.group.name === update.group.name;
      if (groupMatch) {
        const keyMatch = overlayer.key === update.key;
        if (keyMatch) return { ...overlayer, isActive: true };
        else return { ...overlayer, isActive: false };
      } else {
        return overlayer;
      }
    })
  );
});

export const setLayerGroupToNoneAtom = atom(null, (get: Getter, set: Setter, group: LayerGroup) => {
  set(activeOverlayersAtom, () => {
    return get(activeOverlayersAtom).map((overlayer) => {
      if (overlayer.group.name === group.name) return { ...overlayer, isActive: false };
      else return overlayer;
    });
  });
});
